import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { SchoolService } from "../../services/SchoolService";
import { RoleService } from "../../services/RoleService";

const schoolService = new SchoolService();
const roleService = new RoleService();

export default function SchoolEdit() {
  const navigate = useNavigate();
  const { schoolId } = useParams(); // Retrieve schoolId from route params

  const [roles, setRoles] = useState([]);
  const [durations, setDurations] = useState({}); // New state for tracking durations
  const [showReservationsModal, setShowReservationsModal] = useState(false);
  const [reservationsByDay, setReservationsByDay] = useState([]);
  const [loadingReservations, setLoadingReservations] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    type: null,
    active: true,
    address: {
      street: "",
      number: "",
      neighborhood: "",
      city: "",
      zipCode: "",
    },
    availability: [],
    blockedReservationDates: "",
  });

  const isEditMode = !!schoolId;

  // Função para calcular a duração em horas entre dois horários
  const calculateDuration = (startHour, endHour) => {
    if (!startHour || !endHour) return 0;
    
    const [startHours, startMinutes] = startHour.split(':').map(Number);
    const [endHours, endMinutes] = endHour.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    let endTotalMinutes = endHours * 60 + endMinutes;
    
    // Se o horário de término é menor que o de início, é overnight (próximo dia)
    if (endTotalMinutes < startTotalMinutes) {
      endTotalMinutes += 24 * 60; // Adiciona 24 horas
    }
    
    const durationMinutes = endTotalMinutes - startTotalMinutes;
    return Math.round(durationMinutes / 60);
  };

  // Função para verificar se o horário de término é no próximo dia
  const isOvernight = (startHour, endHour, overnightField = null) => {
    // Se o campo overnight já está definido no backend, use-o
    if (overnightField !== null && overnightField !== undefined) {
      return overnightField;
    }
    
    // Caso contrário, calcule baseado nos horários
    if (!startHour || !endHour) return false;
    
    const [startHours, startMinutes] = startHour.split(':').map(Number);
    const [endHours, endMinutes] = endHour.split(':').map(Number);
    
    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    
    return endTotalMinutes < startTotalMinutes;
  };

  // Função para formatar o horário de término com indicação de próximo dia
  const formatEndHour = (startHour, endHour) => {
    if (!startHour || !endHour) return "";

    const isNextDay = isOvernight(startHour, endHour);
    return isNextDay ? `${endHour} (próximo dia)` : endHour;
  };

  // Função para buscar reservas por dia do evento
  const handleViewReservationsByDay = async () => {
    if (!schoolId) return;

    setLoadingReservations(true);
    setShowReservationsModal(true);

    try {
      const data = await schoolService.getEventReservationsByDay(schoolId);
      setReservationsByDay(data.reservationsByDay || []);
    } catch (error) {
      console.error("Error fetching reservations by day:", error);
      alert("Erro ao carregar reservas por dia");
    } finally {
      setLoadingReservations(false);
    }
  };

  // Função para mostrar mensagem de confirmação para reservas overnight
  const showOvernightConfirmation = (startHour, endHour) => {
    if (isOvernight(startHour, endHour)) {
      const duration = calculateDuration(startHour, endHour);
      return `⚠️ Reserva Pernoite detectada: ${startHour} até ${endHour} (${duration} horas, próximo dia)`;
    }
    return null;
  };

  useEffect(() => {
    if (isEditMode) {
      schoolService
        .getSchoolWithUsage(schoolId)
        .then((school) => {
          setFormData(school);

          // Calcular durações baseadas no startHour e endHour
          const calculatedDurations = {};
          if (school.availability && school.availability.length > 0) {
            school.availability.forEach((availability, index) => {
              if (availability.startHour && availability.endHour) {
                calculatedDurations[index] = calculateDuration(availability.startHour, availability.endHour);
              }
            });
          }
          setDurations(calculatedDurations);
        })
        .catch((error) => {
          console.error("Erro ao buscar os dados da escola:", error);
          alert("Falha ao carregar os detalhes da escola/evento.");
          navigate("/admin/schools");
        });
    }

    roleService
      .find()
      .then((response) => {
        const filteredRoles = response.items.filter(
          (role) => role.type !== "AUTHENTICATED"
        );
        setRoles(filteredRoles);

        // ✅ Se for modo de criação, já marca todos os grupos
        if (!isEditMode) {
          setFormData((prev) => ({
            ...prev,
            allowedReservationGroups: filteredRoles.map((role) => role._id),
          }));
        }
      })
      .catch((error) => {
        console.error("Erro ao buscar os papéis:", error);
        alert("Falha ao carregar os papéis de usuário.");
      });
  }, [schoolId, isEditMode, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        [name]: value,
      },
    }));
  };

  const handleAvailabilityChange = (index, field, value) => {
    setFormData((prev) => {
      const newAvailability = [...prev.availability];
      newAvailability[index] = { ...newAvailability[index], [field]: value };
      return { ...prev, availability: newAvailability };
    });

    // If startHour or endHour changes, recalculate duration
    if (field === "startHour" || field === "endHour") {
      const currentAvailability = formData.availability[index];
      const startHour = field === "startHour" ? value : currentAvailability.startHour;
      const endHour = field === "endHour" ? value : currentAvailability.endHour;
      
      if (startHour && endHour) {
        const calculatedDuration = calculateDuration(startHour, endHour);
        setDurations(prev => ({
          ...prev,
          [index]: calculatedDuration
        }));
      }
    }
  };

  const handleDurationChange = (index, value) => {
    setDurations(prev => ({
      ...prev,
      [index]: value
    }));
    
    // Recalcular endHour baseado na nova duração
    const availability = formData.availability[index];
    if (availability.startHour && value) {
      const [hours, minutes] = availability.startHour.split(":").map(Number);
      const totalMinutes = hours * 60 + minutes + (parseInt(value) * 60);
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      
      // Se passar de 24 horas, ajustar para o próximo dia
      const adjustedEndHours = endHours >= 24 ? endHours - 24 : endHours;
      const endHour = `${adjustedEndHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`;
      
      setFormData((prev) => {
        const newAvailability = [...prev.availability];
        newAvailability[index] = { ...newAvailability[index], endHour };
        return { ...prev, availability: newAvailability };
      });
    }
  };

  const handleAddAvailability = () => {
    setFormData((prev) => ({
      ...prev,
      availability: [
        ...prev.availability,
        { weekday: "", startHour: "", endHour: "" },
      ],
    }));
  };

  const handleDeleteAvailability = (index) => {
    setFormData((prev) => {
      const newAvailability = prev.availability.filter((_, i) => i !== index);
      return { ...prev, availability: newAvailability };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Calcular o campo overnight para cada disponibilidade antes de enviar
    const updatedFormData = {
      ...formData,
      availability: formData.availability.map(availability => ({
        ...availability,
        overnight: isOvernight(availability.startHour, availability.endHour)
      }))
    };

    // Remove campos calculados que não devem ser salvos
    delete updatedFormData.usedSlots;
    delete updatedFormData.availableSlots;

    // Garante que slots seja um número se for EVENTO
    if (updatedFormData.type === "EVENTO" && updatedFormData.slots) {
      updatedFormData.slots = Number(updatedFormData.slots);
    }

    if (isEditMode) {
      schoolService
        .updateOne(schoolId, updatedFormData)
        .then(() => {
          alert("Escola/evento atualizada com sucesso!");
          navigate("/admin/schools");
        })
        .catch((error) => {
          console.error("Erro ao atualizar a escola:", error);
          alert("Falha ao atualizar a escola/evento.");
        });
    } else {
      schoolService
        .create(updatedFormData)
        .then(() => {
          alert("Escola/evento criada com sucesso!");
          navigate("/admin/schools");
        })
        .catch((error) => {
          console.error("Erro ao criar a escola:", error);
          alert("Falha ao criar a escola/evento.");
        });
    }
  };

  const handleRoleCheckboxChange = (roleId) => {
    setFormData((prev) => {
      const current = prev.allowedReservationGroups || [];
      const alreadySelected = current.includes(roleId);
      return {
        ...prev,
        allowedReservationGroups: alreadySelected
          ? current.filter((id) => id !== roleId)
          : [...current, roleId],
      };
    });
  };

  return (
    <div className="container">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="m-0">
          {isEditMode ? "Editar Escola / Evento" : "Criar Escola / Evento"}
        </h1>
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={formData.active ?? true} // Se for novo, default true
            onChange={() =>
              setFormData((prev) => ({ ...prev, active: !prev.active }))
            }
          />
          <label className="form-check-label ms-2">
            {formData.active ? "Ativo" : "Inativo"}
          </label>
        </div>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="mb-3">
          <label className="form-label">Nome da Escola / Evento</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Tipo</label>
          <select
            name="type"
            value={formData.type}
            onChange={handleInputChange}
            className="form-control"
            required
          >
            <option value="">Selecione o tipo</option>
            <option value="ESCOLA">ESCOLA</option>
            <option value="EVENTO">EVENTO</option>
          </select>
        </div>

        {formData.type === "EVENTO" && (
          <div className="mb-3">
            <label className="form-label d-flex align-items-center justify-content-between">
              <span>Número de Vagas Para Cada Dia</span>
              {isEditMode && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleViewReservationsByDay}
                >
                  <i className="bi bi-calendar-check me-1"></i>
                  Ver Vagas Reservadas
                </button>
              )}
            </label>
            <input
              type="number"
              name="slots"
              value={formData.slots || ""}
              onChange={handleInputChange}
              className="form-control"
              min="0"
              required
            />
            <small className="text-muted">
              Este é o número de vagas disponíveis para cada dia que o evento estiver ativo
            </small>
          </div>
        )}

        <h4>Endereço</h4>
        <div className="mb-3">
          <label className="form-label">Rua</label>
          <input
            type="text"
            name="street"
            value={formData.address.street}
            onChange={handleAddressChange}
            className="form-control"
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Número</label>
          <input
            type="text"
            name="number"
            value={formData.address.number}
            onChange={handleAddressChange}
            className="form-control"
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Bairro</label>
          <input
            type="text"
            name="neighborhood"
            value={formData.address.neighborhood}
            onChange={handleAddressChange}
            className="form-control"
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">Cidade</label>
          <input
            type="text"
            name="city"
            value={formData.address.city}
            onChange={handleAddressChange}
            className="form-control"
            required
          />
        </div>
        <div className="mb-3">
          <label className="form-label">CEP</label>
          <input
            type="text"
            name="zipCode"
            value={formData.address.zipCode}
            onChange={handleAddressChange}
            className="form-control"
            required
          />
        </div>

        <div className="mb-12">
          <label className="form-label">Datas bloqueadas para reserva</label>
          <input
            type="text"
            name="blockedReservationDates"
            value={formData.blockedReservationDates}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Exemplo: 15/05/2025, 16/05/2025"
          />
        </div>

        <h4>Quais grupos podem reservar nesta escola?</h4>
        <div className="mb-3">
          {roles.map((role) => (
            <div className="form-check" key={role._id}>
              <input
                className="form-check-input"
                type="checkbox"
                id={`role-${role._id}`}
                checked={
                  formData.allowedReservationGroups?.includes(role._id) || false
                }
                onChange={() => handleRoleCheckboxChange(role._id)}
              />
              <label className="form-check-label" htmlFor={`role-${role._id}`}>
                {role.name}
              </label>
            </div>
          ))}
        </div>

        <h4>Disponibilidade</h4>
        
        {/* Alerta para reservas overnight */}
        {formData.availability.some((availability, index) => 
          availability.startHour && availability.endHour && isOvernight(availability.startHour, availability.endHour, availability.overnight)
        ) && (
          <div className="alert alert-warning mb-3" role="alert">
            <strong>⚠️ Reservas Pernoite Detectadas:</strong>
            <ul className="mb-0 mt-2">
              {formData.availability.map((availability, index) => {
                if (availability.startHour && availability.endHour && isOvernight(availability.startHour, availability.endHour, availability.overnight)) {
                  const duration = calculateDuration(availability.startHour, availability.endHour);
                  const weekdays = ['', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];
                  return (
                    <li key={index}>
                      <strong>{weekdays[availability.weekday]}:</strong> {availability.startHour} até {availability.endHour} ({duration} horas, próximo dia)
                    </li>
                  );
                }
                return null;
              })}
            </ul>
          </div>
        )}
        
        {formData.availability.map((availability, index) => (
          <div key={index} className="row mb-3">
            <div className="col-2">
              <label className="form-label">Dia da Semana</label>
              <select
                name={`weekday-${index}`}
                value={availability.weekday}
                onChange={(e) =>
                  handleAvailabilityChange(index, "weekday", e.target.value)
                }
                className="form-select"
                required
              >
                <option value="">Selecione</option>
                <option value="1">Segunda-feira</option>
                <option value="2">Terça-feira</option>
                <option value="3">Quarta-feira</option>
                <option value="4">Quinta-feira</option>
                <option value="5">Sexta-feira</option>
                <option value="6">Sábado</option>
                <option value="7">Domingo</option>
              </select>
            </div>
            <div className="col-2">
              <label className="form-label">Hora de Início</label>
              <input
                type="time"
                name={`startHour-${index}`}
                value={availability.startHour}
                onChange={(e) =>
                  handleAvailabilityChange(index, "startHour", e.target.value)
                }
                className="form-control"
                required
              />
            </div>
            <div className="col-2">
              <label className="form-label">Hora de Término</label>
              <input
                type="time"
                name={`endHour-${index}`}
                value={availability.endHour}
                onChange={(e) =>
                  handleAvailabilityChange(index, "endHour", e.target.value)
                }
                className="form-control"
                required
              />
              {availability.startHour && availability.endHour && (
                <div className="mt-1">
                  <small className="text-muted">
                    {formatEndHour(availability.startHour, availability.endHour)}
                  </small>
                  {isOvernight(availability.startHour, availability.endHour, availability.overnight) && (
                    <span className="badge bg-warning text-dark ms-2">Pernoite</span>
                  )}
                </div>
              )}
            </div>
            <div className="col-2">
              <label className="form-label">Duração (horas)</label>
              <input
                type="number"
                name={`duration-${index}`}
                value={durations[index] || ""}
                onChange={(e) => handleDurationChange(index, e.target.value)}
                className="form-control"
                min="1"
                max="24"
                disabled={!availability.startHour}
                required
              />
              {availability.startHour && durations[index] && (
                <div className="mt-1">
                  <small className="text-muted">
                    Término: {formatEndHour(availability.startHour, availability.endHour)}
                  </small>
                  {isOvernight(availability.startHour, availability.endHour, availability.overnight) && (
                    <span className="badge bg-warning text-dark ms-1">Pernoite</span>
                  )}
                </div>
              )}
              {/* Preview de quando o usuário está digitando a duração */}
              {availability.startHour && !availability.endHour && durations[index] && (
                <div className="mt-1">
                  <small className="text-muted">
                    Preview: {availability.startHour} + {durations[index]}h = {
                      (() => {
                        const [hours, minutes] = availability.startHour.split(":").map(Number);
                        const totalMinutes = hours * 60 + minutes + (parseInt(durations[index]) * 60);
                        const endHours = Math.floor(totalMinutes / 60);
                        const endMinutes = totalMinutes % 60;
                        const adjustedEndHours = endHours >= 24 ? endHours - 24 : endHours;
                        const previewEndHour = `${adjustedEndHours.toString().padStart(2, "0")}:${endMinutes.toString().padStart(2, "0")}`;
                        const isPreviewOvernight = endHours >= 24;
                        return isPreviewOvernight ? `${previewEndHour} (próximo dia)` : previewEndHour;
                      })()
                    }
                  </small>
                  {(() => {
                    const [hours, minutes] = availability.startHour.split(":").map(Number);
                    const totalMinutes = hours * 60 + minutes + (parseInt(durations[index]) * 60);
                    const endHours = Math.floor(totalMinutes / 60);
                    return endHours >= 24 ? (
                      <span className="badge bg-warning text-dark ms-1">Será Pernoite</span>
                    ) : null;
                  })()}
                </div>
              )}
            </div>
            <div className="col-2 d-flex align-items-end">
              <button
                type="button"
                className="btn btn-danger w-100"
                onClick={() => handleDeleteAvailability(index)}
              >
                Remover
              </button>
            </div>
          </div>
        ))}

        <div className="d-flex justify-content-between mt-5">
          <button
            type="button"
            className="btn btn-secondary mb-3"
            onClick={handleAddAvailability}
          >
            Adicionar Disponibilidade
          </button>

          <div>
            <button
              type="button"
              className="btn btn-danger btn-lg"
              onClick={() => navigate("/admin/schools")}
            >
              Cancelar
            </button>

            <button type="submit" className="btn btn-primary btn-lg ms-2">
              {isEditMode ? "Salvar" : "Salvar"}
            </button>
          </div>
        </div>
      </form>

      {/* Modal para mostrar vagas reservadas por dia */}
      {showReservationsModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.5)" }}
        >
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Vagas Reservadas Por Dia</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowReservationsModal(false)}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {loadingReservations ? (
                  <div className="text-center py-4">
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                ) : reservationsByDay.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th className="text-center">Vagas Ocupadas</th>
                          <th className="text-center">Vagas Disponíveis</th>
                          <th className="text-center">Total de Vagas</th>
                          <th className="text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reservationsByDay.map((day, index) => {
                          const date = new Date(day.date + "T00:00:00");
                          const formattedDate = date.toLocaleDateString("pt-BR", {
                            weekday: "long",
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          });

                          const percentageUsed = (day.usedSlots / day.totalSlots) * 100;
                          let statusClass = "text-success";
                          let statusText = "Disponível";

                          if (percentageUsed >= 100) {
                            statusClass = "text-danger";
                            statusText = "Esgotado";
                          } else if (percentageUsed >= 75) {
                            statusClass = "text-warning";
                            statusText = "Quase cheio";
                          }

                          return (
                            <tr key={index}>
                              <td className="text-capitalize">{formattedDate}</td>
                              <td className="text-center">{day.usedSlots}</td>
                              <td className="text-center">{day.availableSlots}</td>
                              <td className="text-center">{day.totalSlots}</td>
                              <td className={`text-center fw-bold ${statusClass}`}>
                                {statusText}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted">
                    <i className="bi bi-calendar-x fs-1 d-block mb-2"></i>
                    <p>Não há reservas futuras para este evento</p>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowReservationsModal(false)}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
